import React, { useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { clearDemoSession, isDemoPersona, seedDemoSession } from '../../demo/core';
import { demoOrganizationFor } from '../../demo/data';
import { getOpsHomePath } from '../../utils/opsHomePath';

/** Ops product accent (DESIGN.md). */
const OPS_ACCENT = '#F4841A';

/**
 * Per-tab ops UI state that could still point at another session's
 * organization, month or onboarding tour. Cleared before the demo is seeded.
 */
const OPS_SESSION_KEYS = ['selectedStats', 'selectedMonth', 'dashboardPreferences', 'ops.onboarding.liveSession'];

function clearOpsSessionState(): void {
  try {
    OPS_SESSION_KEYS.forEach((key) => sessionStorage.removeItem(key));
    const stale: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('ops.onboarding.')) stale.push(key);
    }
    stale.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    /* storage unavailable */
  }
}

/**
 * Public `/demo/:persona` route (`outlet` | `recycler`): starts a demo session in
 * this tab (fake tokens, the demo organization selected) and reloads into the real
 * app on that persona's home page, which then boots on the in-memory fake API.
 */
const DemoEntry: React.FC = () => {
  const { persona } = useParams<{ persona: string }>();

  useEffect(() => {
    if (!isDemoPersona(persona)) {
      window.location.replace('/login');
      return;
    }
    clearDemoSession();
    clearOpsSessionState();
    const organization = demoOrganizationFor(persona);
    seedDemoSession(persona, organization);
    window.location.replace(getOpsHomePath(organization.organization_type));
  }, [persona]);

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        px: 2,
        textAlign: 'center',
        bgcolor: 'background.default',
      }}
    >
      <CircularProgress sx={{ color: OPS_ACCENT }} aria-hidden />
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        Preparing your demo…
      </Typography>
      <Typography variant="body2" color="text.secondary" dir="rtl" lang="ar">
        جارٍ تجهيز العرض التجريبي…
      </Typography>
    </Box>
  );
};

export default DemoEntry;
